/**
 * Cuentas de la calculadora de precio de /organizador (lógica pura, sin
 * React). La comisión de Torneos123 es de $99 por jugador inscrito, pero
 * nunca mayor que el costo de la inscripción: `min(99, costo)`. En el primer
 * torneo no hay comisión.
 */

/** Comisión de Torneos123 por jugador inscrito (MXN). */
export const PLATFORM_FEE_PER_PLAYER = 99;

/** Límites de las entradas de la calculadora. */
export const CALC_LIMITS = { playersMax: 500, costMax: 20_000 } as const;

/** Valores con los que abre la calculadora (cuadran con la Copa Otoño). */
export const CALC_DEFAULTS = { players: 48, cost: 850 } as const;

export interface PriceInput {
  players: number;
  cost: number;
  firstTournament: boolean;
}

export interface PriceBreakdown {
  players: number;
  cost: number;
  /** Lo que se separa por cada jugador: `min(99, costo)`. */
  feePerPlayer: number;
  /** Lo que se cobra en línea: jugadores × costo. */
  collected: number;
  /** Comisión sin la promoción del primer torneo. */
  feeWithoutPromo: number;
  /** Comisión que de verdad se descuenta (0 en el primer torneo). */
  fee: number;
  /** Lo que le queda al organizador antes de la comisión de Mercado Pago. */
  net: number;
  /** true si la promoción del primer torneo quitó una comisión mayor a 0. */
  promoApplied: boolean;
}

/** Entero entre `min` y `max` a partir de lo que se escribió en un campo.
 * Vacío, texto o negativos cuentan como `min`; los decimales se redondean. */
export function toWholeNumber(raw: string | number, min: number, max: number): number {
  const value = typeof raw === "number" ? raw : raw.trim() === "" ? Number.NaN : Number(raw);
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.round(value)));
}

function nonNegativeWhole(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
}

export function calculatePrice({ players, cost, firstTournament }: PriceInput): PriceBreakdown {
  const n = nonNegativeWhole(players);
  const price = nonNegativeWhole(cost);
  const feePerPlayer = Math.min(PLATFORM_FEE_PER_PLAYER, price);
  const collected = n * price;
  const feeWithoutPromo = n * feePerPlayer;
  const fee = firstTournament ? 0 : feeWithoutPromo;
  return {
    players: n,
    cost: price,
    feePerPlayer,
    collected,
    feeWithoutPromo,
    fee,
    net: collected - fee,
    promoApplied: firstTournament && feeWithoutPromo > 0,
  };
}

/* Se arma una sola vez. `minimumFractionDigits: 0` va explícito: sin él, los
   navegadores anteriores a Intl.NumberFormat v3 (p. ej. Safari < 15.4)
   lanzan RangeError con `maximumFractionDigits: 0` en MXN. */
const PESOS = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** $40,800 */
export function formatPesos(amount: number): string {
  return PESOS.format(amount);
}

/** «(48 × $99)» */
export function feeFormula({ players, feePerPlayer }: Pick<PriceBreakdown, "players" | "feePerPlayer">): string {
  return `(${players} × ${formatPesos(feePerPlayer)})`;
}

export interface WorkItem {
  key: "registrations" | "payments" | "welcome";
  count: number;
  label: string;
}

/** «Trabajo que ya no haces a mano». Si la inscripción no cuesta, no hay
 * pagos que conciliar y ese renglón no sale. */
export function manualWorkItems({ players, cost }: Pick<PriceBreakdown, "players" | "cost">): WorkItem[] {
  const one = players === 1;
  const items: WorkItem[] = [
    { key: "registrations", count: players, label: one ? "inscripción que no capturas" : "inscripciones que no capturas" },
  ];
  if (cost > 0) {
    items.push({ key: "payments", count: players, label: one ? "pago que no concilias" : "pagos que no concilias" });
  }
  items.push({
    key: "welcome",
    count: players,
    label: one
      ? "correo de bienvenida con usuario y contraseña que no escribes"
      : "correos de bienvenida con usuario y contraseña que no escribes",
  });
  return items;
}

/** Resumen para lectores de pantalla (región en vivo de la calculadora). */
export function priceSummary(result: PriceBreakdown): string {
  const fee = result.promoApplied
    ? "Comisión Torneos123: $0 por ser tu primer torneo."
    : `Comisión Torneos123: ${formatPesos(result.fee)}.`;
  return `Cobras en línea ${formatPesos(result.collected)}. ${fee} Te queda ${formatPesos(result.net)} antes de la comisión de Mercado Pago.`;
}
